import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Clock,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Users,
} from 'lucide-react'
import { Avatar, initialsFromName } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  useClientActivity,
  useDeleteClientContact,
  useUpdateClientNotes,
} from '@/hooks/useClients'
import { ClientContactForm } from '@/components/clients/ClientContactForm'
import { contactDisplayName, maxContactsForType } from '@/lib/clientContacts'
import { useQuickActions } from '@/hooks/useQuickActions'
import { getApiErrorMessage } from '@/services/api'
import {
  activityIcon,
  activityIconBoxClass,
  activityIconClass,
} from '@/lib/activityIcon'
import { activityRoute, activityTarget } from '@/lib/activityLink'
import { formatPhoneNumber } from '@/lib/phone'
import { activityLabel } from '@/lib/activityLabel'
import { cn } from '@/lib/utils'
import type { ActivityLog, ClientContact, ClientDetail } from '@/types'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

interface ParsedNote {
  meta: string
  body: string
}

function formatActivityTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`
}

function parseClientNotes(notes: string | null | undefined): ParsedNote[] {
  if (!notes?.trim()) return []

  const entries: ParsedNote[] = []
  let current: ParsedNote | null = null

  for (const line of notes.split('\n')) {
    // Stamped notes from addNote / create: "[dd/mm/yyyy HH:mm - Author] body"
    const match = line.match(
      /^\[(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - .+?)\] (.*)$/
    )
    if (match) {
      if (current) entries.push(current)
      current = { meta: match[1], body: match[2] }
      continue
    }

    if (current) {
      // Continuation of the current stamped or freeform block (incl. blank lines).
      current.body = current.body ? `${current.body}\n${line}` : line
    } else if (line.trim()) {
      // Legacy / create-form freeform notes: keep consecutive lines as one entry.
      current = { meta: '', body: line }
    }
  }

  if (current) entries.push(current)
  return entries
}

function ProfileCard({
  icon,
  iconClass,
  title,
  action,
  children,
  className,
}: {
  icon: ReactNode
  iconClass: string
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
        cardShadow,
        className
      )}
    >
      <header className='flex h-16 shrink-0 items-center gap-2 px-6 pb-2 pt-6'>
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-lg p-2',
            iconClass
          )}
        >
          {icon}
        </span>
        <h2 className='min-w-0 flex-1 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
          {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  )
}

// 2.4.6 Profile tab — Figma 2889:56745 (contacts + notes) + 2889:57132 (activity).
export function ProfileTab({
  client,
  canEditClient = false,
  onOpenJob,
  onOpenPayment,
}: {
  client: ClientDetail
  canEditClient?: boolean
  onOpenJob?: (jobId: string, tab?: string) => void
  onOpenPayment?: (paymentId: string) => void
}) {
  const { t } = useTranslation('clients')
  const navigate = useNavigate()
  const { callClient, whatsappClient } = useQuickActions()

  const [activityPage, setActivityPage] = useState(1)
  const [activityLimit, setActivityLimit] = useState(10)
  const { data: activity, isLoading: activityLoading } = useClientActivity(
    client.id,
    { page: activityPage, limit: activityLimit }
  )
  const activityRows = activity?.data ?? []

  const [newNote, setNewNote] = useState('')
  const [contactEditor, setContactEditor] = useState<{
    open: boolean
    contact: ClientContact | null
  }>({ open: false, contact: null })
  const addNote = useUpdateClientNotes(client.id)
  const openActivity = (a: ActivityLog) => {
    const target = activityTarget(a)

    if (target?.type === 'payment' && onOpenPayment) {
      onOpenPayment(target.id)
      return
    }

    if (target?.type === 'job' && onOpenJob) {
      onOpenJob(target.id)
      return
    }

    if (
      (target?.type === 'report' || target?.type === 'job_report') &&
      onOpenJob
    ) {
      onOpenJob(target.id, 'reports')
      return
    }

    const route = activityRoute(a, { clientId: client.id })
    if (route) navigate(route)
  }
  const deleteContact = useDeleteClientContact(client.id)
  const parsedNotes = parseClientNotes(client.notes)
  const contacts = [...(client.contacts ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order
  )
  const canAddContact = contacts.length < maxContactsForType(client.type)

  const onAddNote = async () => {
    const note = newNote.trim()
    if (!note) return
    try {
      await addNote.mutateAsync(note)
      setNewNote('')
      toast({ variant: 'success', title: t('notes.saveSuccess') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('notes.saveError')),
      })
    }
  }

  const onDeleteContact = async (contact: ClientContact) => {
    if (contacts.length <= 1) return
    try {
      await deleteContact.mutateAsync(contact.id)
      toast({ variant: 'success', title: t('form.contactDeleted') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('form.contactDeleteError')),
      })
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid items-stretch gap-4 lg:grid-cols-2'>
        {/* Main Contacts — Figma 2889:57044 */}
        <ProfileCard
          icon={<Users className='size-4 text-[#3271d7]' />}
          iconClass='bg-[#ebf1fe]'
          title={t('mainContacts.title')}
          action={
            canEditClient ? (
              <button
                type='button'
                disabled={
                  !canAddContact &&
                  !(contactEditor.open && !contactEditor.contact)
                }
                onClick={() => {
                  if (contactEditor.open && !contactEditor.contact) {
                    setContactEditor({ open: false, contact: null })
                    return
                  }
                  if (!canAddContact) return
                  setContactEditor({ open: true, contact: null })
                }}
                className='inline-flex shrink-0 items-center gap-2 rounded-lg p-2 text-xs font-medium leading-none text-[#ff5a1f] transition-colors hover:bg-[#fff1ea] disabled:cursor-not-allowed disabled:opacity-40'
              >
                <Plus className='size-4' />
                {t('mainContacts.add')}
              </button>
            ) : undefined
          }
        >
          <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
            {contacts.map(c => {
              const title = contactDisplayName(c, client.name)
              const callTooltip = t('list.actions.call', {
                name: title,
              })
              const whatsappTooltip = t('list.actions.whatsapp')
              const editTooltip = t('mainContacts.actions.edit')
              const deleteTooltip = t('mainContacts.actions.delete')
              return (
                <div
                  key={c.id}
                  className='flex items-start gap-3 rounded-xl border border-[#e7e9ef] bg-white p-3'
                >
                  <Avatar
                    name={title}
                    className='size-8 bg-[#ff5a1f] text-sm font-semibold leading-[1.2] text-white'
                  >
                    {initialsFromName(title)}
                  </Avatar>
                  <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                      {title}
                    </p>
                    {c.role?.trim() ? (
                      <p className='text-xs leading-[1.4] text-[#3d434e]'>
                        {c.role}
                      </p>
                    ) : null}
                    <div className='flex flex-wrap items-center gap-2 text-xs leading-[1.4] text-[#3d434e]'>
                      {c.email?.trim() ? (
                        <span className='truncate'>{c.email}</span>
                      ) : (
                        <span>—</span>
                      )}
                      {c.email?.trim() && c.phone?.trim() && (
                        <span
                          className='size-1 shrink-0 rounded-full bg-[#b0b5c3]'
                          aria-hidden
                        />
                      )}
                      {c.phone?.trim() ? (
                        <span className='truncate'>
                          {formatPhoneNumber(c.phone)}
                        </span>
                      ) : !c.email?.trim() ? null : (
                        <span>—</span>
                      )}
                    </div>
                  </div>
                  <div className='flex shrink-0 items-center gap-2'>
                    <TruncatedTextTooltip
                      alwaysShow
                      closeOnPointerDown
                      className='inline-flex shrink-0'
                      content={callTooltip}
                      delayDuration={0}
                    >
                      <button
                        type='button'
                        aria-label={callTooltip}
                        onClick={() => callClient(c.phone)}
                        className='flex items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-[5px] text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                      >
                        <Phone className='size-4' />
                      </button>
                    </TruncatedTextTooltip>
                    <TruncatedTextTooltip
                      alwaysShow
                      closeOnPointerDown
                      className='inline-flex shrink-0'
                      content={whatsappTooltip}
                      delayDuration={0}
                    >
                      <button
                        type='button'
                        aria-label={whatsappTooltip}
                        onClick={() => whatsappClient(c.phone)}
                        className='flex items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-[5px] transition-colors hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981]'
                      >
                        <WhatsAppIcon width={16} height={16} />
                      </button>
                    </TruncatedTextTooltip>
                    {canEditClient && (
                      <>
                        <TruncatedTextTooltip
                          alwaysShow
                          closeOnPointerDown
                          className='inline-flex shrink-0'
                          content={editTooltip}
                          delayDuration={0}
                        >
                          <button
                            type='button'
                            aria-label={editTooltip}
                            onClick={() =>
                              setContactEditor({ open: true, contact: c })
                            }
                            className='flex items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-[5px] text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                          >
                            <Pencil className='size-4' />
                          </button>
                        </TruncatedTextTooltip>
                        {contacts.length > 1 && (
                          <TruncatedTextTooltip
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                            content={deleteTooltip}
                            delayDuration={0}
                          >
                            <button
                              type='button'
                              aria-label={deleteTooltip}
                              onClick={() => onDeleteContact(c)}
                              className='flex items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-[5px] text-[#171a20] transition-colors hover:border-[#d63b33] hover:bg-[#fef2f2] hover:text-[#d63b33]'
                            >
                              <Trash2 className='size-4' />
                            </button>
                          </TruncatedTextTooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })}
            {contactEditor.open && (
              <ClientContactForm
                client={client}
                contact={contactEditor.contact}
                onClose={() => setContactEditor({ open: false, contact: null })}
              />
            )}
          </div>
        </ProfileCard>

        {/* Internal Notes — Figma 2889:57062 */}
        <ProfileCard
          icon={<MessageSquare className='size-4 text-[#e6a600]' />}
          iconClass='bg-[#fbf3d9]'
          title={t('profile.internalNotes')}
        >
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex max-h-[336px] min-h-0 flex-col gap-2 overflow-y-auto px-6 pb-4 pt-4'>
              {parsedNotes.length === 0 ? (
                <p className='text-sm leading-[1.4] text-[#3d434e]'>
                  {t('notes.noNotes')}
                </p>
              ) : (
                parsedNotes.map((note, i) => (
                  <div
                    key={i}
                    className='flex flex-col gap-2 rounded-xl bg-[#f4f5f8] px-4 py-3'
                  >
                    {note.meta && (
                      <p className='text-xs font-medium leading-4 text-[#3d434e]'>
                        {note.meta}
                      </p>
                    )}
                    <div className='whitespace-pre-wrap wrap-break-word text-sm font-normal leading-[1.4] text-[#171a20]'>
                      {note.body}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className='mt-auto flex shrink-0 items-start gap-4 px-6 pb-6 pt-4'>
              <Textarea
                value={newNote}
                readOnly={!canEditClient}
                onChange={e => setNewNote(e.target.value)}
                placeholder={t('notes.writePlaceholder')}
                maxLength={2000}
                rows={1}
                className={cn(
                  'min-h-10 flex-1 resize-y rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-2.5 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#6c7285] focus-visible:ring-[#ff5a1f]',
                  !canEditClient && 'cursor-default opacity-80'
                )}
              />
              {canEditClient && (
                <Button
                  type='button'
                  variant='ghost'
                  className='h-10 shrink-0 gap-2 rounded-xl px-3 py-3 text-sm font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                  disabled={!newNote.trim() || addNote.isPending}
                  onClick={onAddNote}
                >
                  <Plus className='size-4' />
                  {t('notes.add')}
                </Button>
              )}
            </div>
          </div>
        </ProfileCard>
      </div>

      {/* Recent Activity Preview — Figma 2889:57132 */}
      <ProfileCard
        icon={<Clock className='size-4 text-[#3d434e]' />}
        iconClass='bg-[#f4f5f8]'
        title={t('profile.recentActivity')}
      >
        <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
          {activityLoading ? (
            <p className='text-sm text-[#3d434e]'>{t('activity.loading')}</p>
          ) : activityRows.length === 0 ? (
            <p className='text-sm text-[#3d434e]'>{t('profile.noActivity')}</p>
          ) : (
            activityRows.map(a => {
              const route = activityRoute(a, { clientId: client.id })
              const Icon = activityIcon(a.event_type)
              if (route) {
                return (
                  <button
                    key={a.id}
                    type='button'
                    onClick={() => openActivity(a)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-xl border border-[#e7e9ef] bg-white p-3 text-left transition-colors',
                      'hover:bg-[#fbfbfc]'
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-lg p-2',
                        activityIconBoxClass(a.event_type)
                      )}
                    >
                      <Icon
                        className={cn(
                          'size-4',
                          activityIconClass(a.event_type)
                        )}
                      />
                    </span>
                    <div className='flex min-w-0 flex-1 flex-col gap-1'>
                      <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {activityLabel(a, t)}
                      </p>
                      <p className='text-xs leading-[1.4] text-[#3d434e]'>
                        {formatActivityTime(a.created_at)}
                      </p>
                    </div>
                    <TruncatedTextTooltip
                      alwaysShow
                      closeOnPointerDown
                      className='inline-flex shrink-0'
                      content={t('profile.viewActivity')}
                      delayDuration={0}
                    >
                      <span className='flex shrink-0 items-center justify-center rounded-lg border border-[#e7e9ef] p-1 text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'>
                        <ArrowRight className='size-4' />
                      </span>
                    </TruncatedTextTooltip>
                  </button>
                )
              }

              return (
                <div
                  key={a.id}
                  className='flex w-full items-start gap-3 rounded-xl border border-[#e7e9ef] bg-white p-3 text-left'
                >
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-lg p-2',
                      activityIconBoxClass(a.event_type)
                    )}
                  >
                    <Icon
                      className={cn('size-4', activityIconClass(a.event_type))}
                    />
                  </span>
                  <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                      {activityLabel(a, t)}
                    </p>
                    <p className='text-xs leading-[1.4] text-[#3d434e]'>
                      {formatActivityTime(a.created_at)}
                    </p>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {activity?.metadata && activity.metadata.total > 0 && (
          <ListPagination
            metadata={activity.metadata}
            page={activityPage}
            pageSize={activityLimit}
            onPageChange={setActivityPage}
            onPageSizeChange={size => {
              setActivityLimit(size)
              setActivityPage(1)
            }}
            showShowingRange={false}
            typographyVariant='activityPreview'
            className='rounded-b-2xl border-t border-[#e7e9ef] pl-4 pr-6'
          />
        )}
      </ProfileCard>
    </div>
  )
}
